import React from 'react';
import { 
  XMarkIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { MissionPatient, Priority } from '../../types';
import clsx from 'clsx';

interface PatientDeleteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  patient: MissionPatient | null;
  isLoading?: boolean;
}

const PatientDeleteModal: React.FC<PatientDeleteModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  patient,
  isLoading = false
}) => {
  if (!isOpen || !patient) return null;

  const getPriorityColor = (priority?: string) => {
    switch (priority) {
      case Priority.CRITICAL:
        return 'text-red-600 bg-red-50';
      case Priority.URGENT:
        return 'text-yellow-600 bg-yellow-50';
      case Priority.ROUTINE:
        return 'text-green-600 bg-green-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">
              Delete Patient
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
              disabled={isLoading}
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
        </div>

        <div className="p-6">
          <div className="flex items-center mb-4">
            <div className="flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
              <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
            </div>
            <div className="ml-4 flex-1">
              <p className="text-sm font-medium text-gray-900">
                Are you sure you want to delete Patient #{patient.patientNumber}?
              </p>
              <p className="text-sm text-gray-500 mt-1">
                This action cannot be undone.
              </p>
            </div>
          </div>

          {/* Patient Details */}
          <div className="bg-gray-50 rounded-lg p-4 mb-4">
            <h4 className="font-medium text-gray-900 mb-3 flex items-center">
              Patient #{patient.patientNumber}
              {patient.triagePriority && (
                <span className={clsx(
                  'ml-2 px-2 py-0.5 rounded-full text-xs font-medium',
                  getPriorityColor(patient.triagePriority)
                )}>
                  {patient.triagePriority}
                </span>
              )}
            </h4>
            
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Age:</span>
                <span className="ml-2 font-medium">{patient.age || 'Unknown'} years</span>
              </div>
              
              <div>
                <span className="text-gray-600">Gender:</span>
                <span className="ml-2 font-medium">{patient.gender || 'Unknown'}</span>
              </div>
              
              {patient.weight && (
                <div className="col-span-2">
                  <span className="text-gray-600">Weight:</span>
                  <span className="ml-2 font-medium">{patient.weight} kg</span>
                </div>
              )}
              
              {patient.chiefComplaint && (
                <div className="col-span-2">
                  <span className="text-gray-600">Chief Complaint:</span>
                  <span className="ml-2 font-medium">{patient.chiefComplaint}</span>
                </div>
              )}
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
            <div className="flex">
              <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 flex-shrink-0" />
              <div className="ml-2">
                <p className="text-sm text-yellow-800 font-medium">
                  Important: Original patient numbering will be preserved
                </p>
                <p className="text-sm text-yellow-700 mt-1">
                  Remaining patients will keep their original numbers (e.g., deleting Patient #2 will leave Patient #1 and #3).
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="px-6 py-3 bg-gray-50 border-t border-gray-200 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="btn btn-secondary"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className={clsx(
              'btn btn-primary bg-red-600 hover:bg-red-700 focus:ring-red-500',
              isLoading && 'opacity-50 cursor-not-allowed'
            )}
          >
            {isLoading ? 'Deleting...' : 'Delete Patient'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PatientDeleteModal;